import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, ChevronDown, User, LogOut, Users } from "lucide-react";
import logo from "../assets/logos/logo.png";
import { api } from "../services";
import { logoutSession } from "../services/authSession";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import "./OrdersDisplay.css"; // Import filter button styles
import { usePermission } from "../hooks/usePermission";
import { getFirstAccessibleTenantPath, RBAC_MODULES, PERMISSION_ACTIONS } from "../config/routePermissions";
import { useSetupGuideStatus } from "../hooks/useSetupGuideStatus";
import { isCompanyTenantUser } from "../utils/tenantUserType";
import "./onboarding/SetupGuidePage.css";

function TopNavbar({
  onSidebarToggle,
  onDesktopSidebarToggle,
  desktopSidebarCollapsed,
  partnerSessionMode = false,
}) {
  const { t, i18n } = useTranslation("translation");
  const { can, bypass } = usePermission();
  const [username, setUsername] = useState("");
  const [userRole, setUserRole] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isRTL, setIsRTL] = useState(document.documentElement.dir === "rtl");
  const [isLoggedIn, setIsLoggedIn] = useState(
    !!localStorage.getItem("token") || !!localStorage.getItem("adminToken")
  );
  const [ordersDisplayFilters, setOrdersDisplayFilters] = useState(null);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isAdminPath = pathname.startsWith("/admin");
  const dropdownRef = useRef(null);
  const role = localStorage.getItem("active_role") === "user";
  const adminToken = localStorage.getItem("adminToken");
  // Function to check authentication status
  const checkAuthStatus = () => {
    const loggedIn = !!localStorage.getItem("token") || !!localStorage.getItem("adminToken");
    setIsLoggedIn(loggedIn);
    return loggedIn;
  };

  // 🔄 Sync isLoggedIn on custom auth events and storage changes
  useEffect(() => {
    // Initial check
    checkAuthStatus();

    // Listen for custom auth change events
    const handleAuthChange = () => checkAuthStatus();
    window.addEventListener("authChange", handleAuthChange);
    
    // Listen for storage changes (useful for cross-tab synchronization)
    const handleStorageChange = (e) => {
      if (e.key === "token" || e.key === "adminToken") {
        checkAuthStatus();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    // Custom event for login (trigger this after successful login)
    const handleLoginEvent = () => checkAuthStatus();
    window.addEventListener("userLogin", handleLoginEvent);

    return () => {
      window.removeEventListener("authChange", handleAuthChange);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("userLogin", handleLoginEvent);
    };
  }, []);

  // Detect RTL changes live
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsRTL(document.documentElement.dir === "rtl");
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["dir"],
    });
    return () => observer.disconnect();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch profile data when logged in
  useEffect(() => {
    if (isLoggedIn) {
      const fetchUserProfile = async () => {
        try {
          const token = isAdminPath
            ? localStorage.getItem("adminToken")
            : localStorage.getItem("token");

          if (!token) return;

          const response = await api.get("/profile", {
            headers: { Authorization: `Bearer ${token}` },
          });

          setUsername(response.data.username || "User");
          setUserRole(response.data.role || "");
        } catch (error) {
          console.error("Error fetching user profile:", error);
          if (!pathname.includes("/login")) {
            handleLogout();
          }
        }
      };

      fetchUserProfile();
    } else {
      setUsername("");
      setUserRole("");
    }
  }, [isLoggedIn, isAdminPath, pathname]);

  // Listen for OrdersDisplay filters updates
  useEffect(() => {
    const handleFiltersUpdate = () => {
      if (pathname === '/orders-display' && window.ordersDisplayFilters) {
        setOrdersDisplayFilters(window.ordersDisplayFilters);
      } else {
        setOrdersDisplayFilters(null);
      }
    };

    // Initial check
    handleFiltersUpdate();

    // Listen for updates
    window.addEventListener('ordersDisplayFiltersUpdated', handleFiltersUpdate);

    return () => {
      window.removeEventListener('ordersDisplayFiltersUpdated', handleFiltersUpdate);
    };
  }, [pathname]);

  const handleLogout = async () => {
    await logoutSession();
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminLogin");
    localStorage.removeItem("active_role");
    setIsLoggedIn(false);
    setDropdownOpen(false);
    window.dispatchEvent(new Event("authChange")); // 🔥 update navbar instantly
    navigate(isAdminPath ? "/admin/login" : "/");
  };

  const handleLogoClick = () => {
    if (isAdminPath) {
      navigate("/admin/user-list");
    } else if (isLoggedIn) {
      navigate(getFirstAccessibleTenantPath(can));
    } else {
      navigate("/");
    }
  };

  const handleProfileClick = () => {
    setDropdownOpen(false);
    navigate(isAdminPath ? "/admin/profile" : "/profile");
  };

  const handleMyAccountClick = () => {
    setDropdownOpen(false);
    navigate("/my-account");
  };

  const showMyAccount =
    !isAdminPath &&
    isLoggedIn &&
    (bypass || can(RBAC_MODULES.ACCOUNT, PERMISSION_ACTIONS.VIEW));

  const isCompanyUser = isCompanyTenantUser();

  const showSetupGuide =
    !partnerSessionMode &&
    !isAdminPath &&
    isLoggedIn &&
    isCompanyUser &&
    (bypass || can(RBAC_MODULES.DASHBOARD, PERMISSION_ACTIONS.VIEW));

  const { remainingCoreSteps, progress, allCoreStepsComplete } = useSetupGuideStatus({
    enabled: showSetupGuide,
  });

  const handleSidebarToggleUniversal = () => {
    // If on orders-display page, navigate to dashboard instead
    if (pathname === "/orders-display") {
      navigate(getFirstAccessibleTenantPath(can));
      return;
    }
    
    // Otherwise, toggle sidebar as normal
    if (window.innerWidth >= 1024) {
      onDesktopSidebarToggle && onDesktopSidebarToggle();
    } else {
      onSidebarToggle && onSidebarToggle();
    }
  };

  return (
    <div
      style={{
        backgroundColor: "white",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        padding: "8px 20px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px"
        }}
      >
        {/* Left side (Menu + Logo) */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {isLoggedIn && !partnerSessionMode && (
            <span
              style={{
                cursor: "pointer",
                background: pathname === "/orders-display" ? "transparent" : "#1c1c1c",
                borderRadius: "20%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                color: pathname === "/orders-display" ? "#000" : "#fff",
              }}
              onClick={handleSidebarToggleUniversal}
            >
              <Menu size={20} />
            </span>
          )}

          <div
            onClick={partnerSessionMode ? () => navigate(getFirstAccessibleTenantPath(can)) : handleLogoClick}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
            }}
          >
            <img
              src={logo}
              alt="Company Logo"
              style={{ height: "32px", width: "auto" }}
            />
            {isAdminPath && isLoggedIn && (
              <span
                style={{
                  backgroundColor: "red",
                  color: "white",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  fontSize: "0.75rem",
                }}
              >
                {t("Admin")}
              </span>
            )}
          </div>
        </div>

        {/* Center - OrdersDisplay Filters - Show when on orders-display page and not in fullscreen */}
        {ordersDisplayFilters && !ordersDisplayFilters.isFullscreen && (
          <div 
            className="topnav-filters-container"
            style={{ display: 'flex', gap: '12px', flexWrap: 'nowrap', flex: '1', justifyContent: 'center', alignItems: 'center' }}
          >
            <button
              onClick={() => ordersDisplayFilters.handleFilterChange("all")}
              className={`filter-btn ${ordersDisplayFilters.activeFilter === "all" ? "active orange" : "orange"}`}
            >
              {t("all")}
            </button>

            <button
              onClick={() => ordersDisplayFilters.handleFilterChange("pending")}
              className={`filter-btn ${ordersDisplayFilters.activeFilter === "pending" ? "active" : ""}`}
            >
              {t("New")} <span>{ordersDisplayFilters.orderStats?.PENDING || 0}</span>
            </button>

            <button
              onClick={() => ordersDisplayFilters.handleFilterChange("in_progress")}
              className={`filter-btn ${ordersDisplayFilters.activeFilter === "in_progress" ? "active orange" : "orange"}`}
            >
              {t("inProgress")} <span>{ordersDisplayFilters.orderStats?.ACCEPTED || 0}</span>
            </button>

            <button
              onClick={() => ordersDisplayFilters.handleFilterChange("ready")}
              className={`filter-btn ${ordersDisplayFilters.activeFilter === "ready" ? "active blue" : "blue"}`}
            >
              {t("completed")}
            </button>
          </div>
        )}

        {/* Right side (Language + Profile) */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            ...(isRTL ? { marginRight: "auto" } : { marginLeft: "auto" }),
          }}
        >
          {partnerSessionMode && (
            <button
              type="button"
              className="partner-dash-btn"
              onClick={() => navigate(getFirstAccessibleTenantPath(can))}
            >
              <span>{t("partnerFlow.openDashboard")}</span>
              <span className="partner-dash-btn__icon" aria-hidden="true">↗</span>
            </button>
          )}

          {showSetupGuide && (
            <button
              type="button"
              className="setup-guide-chip"
              onClick={() => navigate("/setup-guide")}
            >
              <span
                className="setup-guide-chip__ring"
                style={{
                  background: `conic-gradient(#073B4C ${
                    progress?.totalCoreSteps
                      ? ((progress.completedCoreSteps || 0) / progress.totalCoreSteps) * 360
                      : allCoreStepsComplete
                        ? 360
                        : 0
                  }deg, #E3E9EB 0deg)`,
                }}
                aria-hidden="true"
              >
                <span className="setup-guide-chip__ring-inner" />
              </span>
              <span className="setup-guide-chip__text">
                <span className="setup-guide-chip__title">{t("setupGuide.headerLink")}</span>
                <span className="setup-guide-chip__remaining">
                  {allCoreStepsComplete
                    ? t("setupGuide.status.completed")
                    : t(
                        remainingCoreSteps === 1
                          ? "setupGuide.headerRemainingOne"
                          : "setupGuide.headerRemaining",
                        { count: remainingCoreSteps ?? 0 }
                      )}
                </span>
              </span>
            </button>
          )}

          <LanguageSwitcher />

          {isLoggedIn && (
            <div style={{ position: "relative" }} ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen((prev) => !prev)}
                className="profile-btn"
              >
                <div className="profile-avatar">
                  <User size={14} />
                </div>
                <div
                  className="profile-info"
                  style={{ alignItems: isRTL ? "flex-end" : "flex-start" }}
                >
                  <span className="profile-username">{username}</span>
                  {userRole && <small className="profile-role">{userRole}</small>}
                </div>
                <ChevronDown size={16} className="profile-chevron" />
              </button>

              {dropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "110%",
                    [isRTL ? "left" : "right"]: 0,
                    backgroundColor: "white",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                    zIndex: 1000,
                    minWidth: "180px",
                    padding: "4px 0",
                  }}
                >
                  {/* My Account */}
                  {showMyAccount ? (
                    <div
                      onClick={handleMyAccountClick}
                      style={{
                        padding: "8px 12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        color: "#333",
                        transition: "background-color 0.2s ease",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f8f9fa")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.backgroundColor = "white")
                      }
                    >
                      <Users size={14} />
                      <span>{t("myAccount.title")}</span>
                    </div>
                  ) : null}

                  {/* Profile Link */}
                  <div
                    onClick={handleProfileClick}
                    style={{
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      color: "#333",
                      transition: "background-color 0.2s ease"
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "white")}
                  >
                    <User size={14} />
                    <span>{t("Profile Settings")}</span>
                  </div>

                  {/* Switch to Admin - Only show when not in admin path */}
                  {!isAdminPath && role && adminToken && (
                    <div
                        onClick={() => {
                        // Remove regular user token and update active_role before redirecting to admin
                        localStorage.removeItem('token');
                        localStorage.setItem('active_role', 'admin');
                        setDropdownOpen(false);
                        navigate("/admin/user-list");
                      }}
                      style={{
                        padding: "8px 12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        color: "#333",
                        borderTop: "1px solid #f0f0f0",
                        transition: "background-color 0.2s ease"
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
                      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "white")}
                    >
                      <Users size={14} />
                      <span>{t("Switch to Admin")}</span>
                    </div>
                  )}

                  {/* Logout Button */}
                  <div
                    onClick={handleLogout}
                    style={{
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                      color: "#dc3545",
                      borderTop: "1px solid #f0f0f0",
                      transition: "background-color 0.2s ease"
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
                    onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "white")}
                  >
                    <LogOut size={14} />
                    <span>{t("Logout")}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TopNavbar;